/**
 * AddonsSection — lista los add-ons disponibles para el plan actual y permite
 * activarlos/desactivarlos.
 *
 * Contrato backend (autoritativo):
 *   GET  /addons?planId={planId}            -> { data: AddOn[] }
 *   POST /subscriptions/{subId}/addons      -> { addOnId, action: 'activate'|'deactivate' }
 *
 * AddOn = { addOnId, name, description, pricing: { USD: { monthly } },
 *           availableFrom, featureFlags, limitsModifier, active }
 *
 * El estado "activo" se deriva de subscription.limits (featureFlags ⊆ limits).
 * Una feature incluida nativamente por el plan (no comprada como add-on) se
 * muestra como "Incluido en tu plan" y no se puede togglear desde acá.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getAddons, toggleAddon } from '../services/api';
import './AddonsSection.css';

const ADDON_ICONS = {
  'afip-invoicing': '🧾',
  delivery: '🚚',
  'customer-app': '📱',
  'extra-restaurant': '🏪',
  'advanced-inventory': '📦',
};

const PLAN_ORDER = { free: 1, starter: 2, professional: 3, business: 4, enterprise: 5 };

// Plan que incluye nativamente cada feature flag (mantener sync con backend
// services/.../middleware/featureGate.js FEATURE_REQUIRED_PLAN). Si el plan
// actual es >= a este nivel, la feature viene incluida y no es un add-on.
const NATIVE_PLAN_BY_FLAG = {
  deliveryEnabled: 'business',
  customerAppEnabled: 'business',
  recipesEnabled: 'business',
  afipInvoicingEnabled: 'professional',
};

const planLevel = (planId) => PLAN_ORDER[planId] || 0;

function AddonsSection({
  subscriptionId,
  currentPlanId,
  currency = 'USD',
  subscriptionLimits,
  variant = 'dark',
  onAddonChange,
}) {
  const { t } = useTranslation();
  const [addons, setAddons] = useState([]);
  const [limits, setLimits] = useState(subscriptionLimits || {});
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [error, setError] = useState('');

  // Keep local limits in sync if the parent reloads the subscription.
  useEffect(() => {
    setLimits(subscriptionLimits || {});
  }, [subscriptionLimits]);

  const getCurrencySymbol = (curr) => {
    switch (curr) {
      case 'ARS':
        return 'AR$';
      case 'BRL':
        return 'R$';
      default:
        return '$';
    }
  };

  const symbol = getCurrencySymbol(currency);

  const loadAddons = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getAddons(currentPlanId).catch(() => ({ data: [] }));
      setAddons(result.data || []);
    } catch {
      setAddons([]);
    } finally {
      setLoading(false);
    }
  }, [currentPlanId]);

  useEffect(() => {
    loadAddons();
  }, [loadAddons]);

  // ¿La feature del add-on viene incluida nativamente por el plan actual?
  const isIncludedNatively = (addon) => {
    const flags = Object.keys(addon.featureFlags || {});
    return flags.some((flag) => {
      const native = NATIVE_PLAN_BY_FLAG[flag];
      return native && planLevel(currentPlanId) >= planLevel(native);
    });
  };

  // ¿El add-on está activo (comprado)? Todas sus featureFlags están en limits.
  const isAddonActive = (addon) => {
    const flags = Object.keys(addon.featureFlags || {});
    if (flags.length === 0) return false; // ej: extra-restaurant (solo limitsModifier)
    return flags.every((flag) => limits?.[flag] === true);
  };

  const handleToggle = async (addon) => {
    if (!subscriptionId || toggling) return;
    setError('');
    const activate = !isAddonActive(addon);
    try {
      setToggling(addon.addOnId);
      const result = await toggleAddon(subscriptionId, addon.addOnId, activate);
      // El backend devuelve los limits actualizados — usarlos como fuente de verdad.
      if (result?.subscription?.limits) {
        setLimits(result.subscription.limits);
      }
      if (onAddonChange) onAddonChange();
    } catch (err) {
      console.error('Error toggling addon:', err);
      setError(err.response?.data?.message || t('planSelection.addons.toggleError'));
    } finally {
      setToggling(null);
    }
  };

  if (loading) return null;
  if (addons.length === 0) return null;

  return (
    <div className={`addons-section addons-section--${variant}`}>
      <h2 className="addons-title">{t('planSelection.addons.title')}</h2>
      <p className="addons-subtitle">{t('planSelection.addons.subtitle')}</p>

      {error && <div className="addons-error">{error}</div>}

      <div className="addons-grid">
        {addons.map((addon) => {
          const included = isIncludedNatively(addon);
          const isActive = !included && isAddonActive(addon);
          const pricing = addon.pricing?.[currency] || addon.pricing?.USD || {};
          const isBusy = toggling === addon.addOnId;

          return (
            <div
              key={addon.addOnId}
              className={`addon-card ${isActive ? 'addon-active' : ''} ${
                included ? 'addon-included' : ''
              }`}
            >
              <div className="addon-header">
                <span className="addon-icon">{ADDON_ICONS[addon.addOnId] || '🔧'}</span>
                <div className="addon-info">
                  <h3 className="addon-name">
                    {t(`planSelection.addons.items.${addon.addOnId}.name`, addon.name)}
                  </h3>
                  <p className="addon-description">
                    {t(
                      `planSelection.addons.items.${addon.addOnId}.description`,
                      addon.description
                    )}
                  </p>
                </div>
              </div>
              <div className="addon-footer">
                {included ? (
                  <span className="addon-included-label">
                    {t('planSelection.addons.includedInPlan')}
                  </span>
                ) : (
                  <>
                    <span className="addon-price">
                      {pricing.monthly != null
                        ? `${symbol}${pricing.monthly}/${t('planSelection.month')}`
                        : t('planSelection.contactUs')}
                    </span>
                    <button
                      type="button"
                      className={`btn ${isActive ? 'btn-secondary' : 'btn-primary'} btn-sm`}
                      onClick={() => handleToggle(addon)}
                      disabled={isBusy || !subscriptionId}
                    >
                      {isBusy
                        ? t('planSelection.processing')
                        : isActive
                          ? t('planSelection.addons.deactivate')
                          : t('planSelection.addons.activate')}
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AddonsSection;
